/* eslint-disable no-useless-escape */
import {
  ChangeDetectorRef,
  Component, EventEmitter,
  Input, OnDestroy,
  OnInit, Optional,
  Output, Self,
  ViewChild, ViewEncapsulation
} from '@angular/core';
import {AbstractControl, ControlValueAccessor, FormControl, NgControl} from '@angular/forms';
import {BehaviorSubject, combineLatest, Observable, of, Subject, timer, merge} from 'rxjs';
import {debounce, filter, map, shareReplay, startWith, switchMap, takeUntil, tap, catchError, take} from 'rxjs/operators';
import {ItemListProvider, MultiSelectSearchDropdownOption} from './item-list.provider';
import {MatSelect, MatSelectConfig, MAT_SELECT_CONFIG} from '@angular/material/select';
import {MessageService} from '../../../shared/services/message-service.service';

export type SelectSearchDropdownOptionsInput = string | {label: string; value: string};

@Component({
  selector: 'app-multiselect-search-dropdown',
  templateUrl: './multiselect-search-dropdown.component.html',
  styleUrls: ['./multiselect-search-dropdown.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: MAT_SELECT_CONFIG,
      useValue: {
        disableOptionCentering: true
      } as MatSelectConfig
    }
  ]
})
export class MultiSelectSearchDropdownComponent implements OnInit, OnDestroy, ControlValueAccessor {

  @Input() public set value(value: string[]) {
    this.control?.setValue(value || []);
    if (value.length === 0) {
      // tslint:disable-next-line: no-unused-expression
      this.matSelect && ((this.matSelect as unknown) as any)._propagateChanges();
    }
    this.cd.markForCheck();
  }
  public get value(): string[] {
    return this.control?.value;
  }

  @Output() public valueChange = new EventEmitter<string[]>();

  public filterControl: FormControl = new FormControl('');

  public isOverlayOpened: boolean;

  @Input() itemListProvider: ItemListProvider;
  @Input() useCustomToggler = true;
  @Input() testId: string = null;
  @Input() label: string = null;
  @Input() isRequired = false;
  @Input() useSelectAll = false;
  @Input() disableOptionCentering = null;
  @Input() inputHint: string = null;
  @Input() inputHintIcon: string = null;

  @ViewChild('matSelect', {read: MatSelect}) matSelect: MatSelect;

  private cachedValues = new Map<string, string>();
  private ngUnsubscribe = new Subject<void>();

  private localControl: AbstractControl = new FormControl([]);
  public get control() {
    if (this.ngControl) {
      return this.ngControl.control;
    }
    return this.localControl;
  }

  constructor(@Self() @Optional() private ngControl: NgControl, private cd: ChangeDetectorRef, private messageService: MessageService) {
    if (ngControl) {
      ngControl.valueAccessor = this;
    }
  }

  selectedItemList$: Observable<MultiSelectSearchDropdownOption[]>;
  private loadingSubject = new BehaviorSubject(false);
  selectedItemListLoading$ = this.loadingSubject.asObservable();
  selectionListCombined$: Observable<MultiSelectSearchDropdownOption[]>;

  searchedItemList$: Observable<MultiSelectSearchDropdownOption[] & {showNotFound: boolean}>;

  writeValue(obj: any): void {}
  registerOnChange(fn: any): void {}
  registerOnTouched(fn: any): void {}

  ngOnInit() {
    /**
     * Control input value changes
     */
    this.selectedItemList$ = combineLatest([
      // tslint:disable-next-line:deprecation
      merge(of(this.filterControl.value), this.filterControl.valueChanges),
      this.control.valueChanges.pipe(
        startWith(this.control.value || []),
        switchMap((value: string[]) => {
          const usernamesToLoad = value.filter(v => !this.cachedValues.has(v));
          if (usernamesToLoad.length) {
            this.loadingSubject.next(true);
            return this.itemListProvider.loadValues(value).pipe(
              tap(() => this.loadingSubject.next(false)),
              tap(fetched => {
                this.addToCached(fetched);
              }),
              map(() => this.assignLabels(value)),
              catchError(() => of([] as MultiSelectSearchDropdownOption[]))
            );
          }
            return of(this.assignLabels(value));

        }),
        tap(() => {
          this.loadingSubject.next(false);
        })
      )
    ]).pipe(
      map(([search, options]) => options.map(v => {
          if (!search) {
            return v;
          }

          const regex = new RegExp(search.replace(/[-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&'), 'i');
          return {...v, label: v.label.replace(regex, '<b>$&</b>')};
        })),
        takeUntil(this.ngUnsubscribe),
        shareReplay({
          bufferSize: 1,
          refCount: false
        })
    );

    /**
     * Search item list
     */
    this.searchedItemList$ = combineLatest([
      this.filterControl.valueChanges.pipe(
        filter(v => typeof v === 'string'),
        debounce(v => (v.length ? timer(300) : timer(0))),
        switchMap((value: string) => {
          if (value.length === 0) {
            return of<MultiSelectSearchDropdownOption[]>([]);
          }

          this.loadingSubject.next(true);
          return this.itemListProvider.searchValues(value).pipe(
            tap(() => this.loadingSubject.next(false)),
            tap(fetched => {
              this.addToCached(fetched);
            }),
            catchError(() => of([]))
          );
        }),
        tap(() => {
          this.loadingSubject.next(false);
        })
      ),
      this.control.valueChanges.pipe(startWith(this.control.value || []))
    ]).pipe(
      map(([list, value]) => {
        const visibleList = list
          .filter(v => value.indexOf(v.value) === -1)
          .map(v => {
            const search: string = this.filterControl.value;
            if (!search) {
              return v;
            }

            const regex = new RegExp(search, 'i');
            return {...v, label: v.label.replace(regex, '<b>$&</b>')};
          });

        return Object.assign(visibleList, {
          showNotFound: list.length === 0 && this.filterControl.value?.length > 0
        });
      }),
      takeUntil(this.ngUnsubscribe),
      shareReplay({
        bufferSize: 1,
        refCount: false
      })
    );

    this.selectionListCombined$ = combineLatest([this.selectedItemList$, merge(of([]), this.searchedItemList$)]).pipe(
      map(([a, b]) => [...a, ...b]),
      takeUntil(this.ngUnsubscribe),
      shareReplay({
        bufferSize: 1,
        refCount: false
      })
    );
  }

  private addToCached(fetched: Array<any>) {
    fetched.forEach(v => {
      this.cachedValues.set(v.value, v.label);
    });
  }

  private assignLabels(value: string[]): MultiSelectSearchDropdownOption[] {
    return value.map(v => ({
      value: v,
      label: this.cachedValues.get(v)
    }));
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public openedChange(opened: boolean) {
    this.isOverlayOpened = opened;

    if (!opened) {
      this.valueChange.emit(this.control.value);
    }
  }

  public removeAllSelectedItems(event: Event): void {
    event.stopPropagation();

    this.control.setValue([]);
    this.filterControl.setValue('');
    this.valueChange.emit(this.control.value);
    ((this.matSelect as unknown) as any)._propagateChanges(); // refreshed the MatOption state
  }

  public trackByItem(index: number, element: MultiSelectSearchDropdownOption) {
    return element.value;
  }

  public toggleSelectAll($event: boolean) {
    if (!$event) {
      this.control.setValue([]);
      this.valueChange.emit(this.control.value);
      ((this.matSelect as unknown) as any)._propagateChanges(); // refreshed the MatOption state
    } else {
      this.selectionListCombined$.pipe(take(1)).subscribe(fil => {
        this.control.setValue(fil.map(i => i.value));
        this.valueChange.emit(this.control.value);
        fil.forEach(i => {
          ((this.matSelect as unknown) as any)._propagateChanges(i);
        });
      });
    }
  }

  reset(): void {
    this.control.setValue([]);
    this.valueChange.emit(this.control.value);
  }


  public getErrorMessage(): string {
    if (this.control && this.control.errors) {
      return this.messageService.getControlErrorMessage(this.control.errors);
    }  return null;
  }

}
